import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { blocksClient } from '@/lib/blocks/client';

export interface AssignableRole {
  itemId: string;
  name: string;
  slug: string;
}

const asRecordArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? (value as Record<string, unknown>[]) : [];

/** IAM returns `{ hierarchy: [...], standalone: [...] }`; a flat array or `{ data: [...] }` is also tolerated. */
const collectRoles = (response: unknown): Record<string, unknown>[] => {
  if (Array.isArray(response)) return response as Record<string, unknown>[];
  const { hierarchy, standalone, data } = (response ?? {}) as Record<string, unknown>;
  return [...asRecordArray(hierarchy), ...asRecordArray(standalone), ...asRecordArray(data)];
};

const normalizeRoles = (response: unknown): AssignableRole[] => {
  const bySlug = new Map<string, AssignableRole>();
  collectRoles(response).forEach((role) => {
    if (typeof role.slug !== 'string' || typeof role.name !== 'string') return;
    bySlug.set(role.slug, {
      itemId: typeof role.itemId === 'string' ? role.itemId : role.slug,
      name: role.name,
      slug: role.slug,
    });
  });
  return [...bySlug.values()];
};

/** Roles the signed-in user is allowed to assign to other users, from Blocks OS IAM. */
export const useAssignableRoles = () =>
  useQuery({
    queryKey: ['iam', 'roles', 'assignable'],
    queryFn: async () => normalizeRoles(await blocksClient.iam.roles.assignable()),
    staleTime: 5 * 60 * 1000,
  });

// Role slugs are tenant-defined data (a custom role like "app_user" has no translation), not static UI
// copy, so they are never passed through `t()`. IAM's own configured display name is used when known,
// falling back to a humanized version of the slug (`app_user` -> `App user`) rather than the raw slug.
const humanizeRoleSlug = (slug: string) =>
  slug
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/^\w/, (char) => char.toUpperCase());

/** Display name for a role slug: IAM's role name when known, else the humanized slug. */
export const useRoleName = () => {
  const { data: assignableRoles = [] } = useAssignableRoles();
  const roleNameBySlug = useMemo(
    () => new Map(assignableRoles.map((role) => [role.slug, role.name])),
    [assignableRoles]
  );
  return useCallback(
    (slug: string) => roleNameBySlug.get(slug) ?? humanizeRoleSlug(slug),
    [roleNameBySlug]
  );
};
